import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { InvoicingDetailSheetData } from "../../_components/view/InvoiceDetailsSheet";
import { useInvoicingContext } from "../../context/InvoicingContext";

import { TFetchWrapperProps } from "@/components/reusables/FetchWrapper";
import {
  InvoicingActions,
  InvoicingLocalKeys,
  InvoicingQueryParams,
} from "@/constants/invoicing.constants";
import useQueryParams from "@/hooks/useQueryParams";
import { IProject } from "@/interfaces/project.interface";
import {
  useGetMultiContractQuery,
  useGetSingleContractInvoiceQuery,
  useMuateContractInvoiceMutation,
} from "@/redux/api/invoicingQuery";
import {
  CreateDocketInvoicing,
  emptyInvoicing,
  emptyInvoicingItems,
} from "@/schemas/invoicing.schema";
import { combineDateWithCurrentTime } from "@/utils/helper-functions/date-time.helper";
import {
  filterEmptyValues,
  parseToFormData,
} from "@/utils/helper-functions/form.helper";

// Types
type MutationResponse =
  | { data: object; error?: undefined }
  | { data?: undefined; error: FetchBaseQueryError | SerializedError };

type ContractInvoicingContextType = {
  formattedFormDefaultValues: CreateDocketInvoicing;
  isLoading: Omit<TFetchWrapperProps, "children">;
  mutateDocketInvoice: (
    data: CreateDocketInvoicing,
    dirtyFields?: object,
  ) => Promise<MutationResponse>;
  setSelectedProjectId: (projectId: string) => void;
  isMutating: boolean;
  projects: IProject[];
  formattedInvoicingDetails: InvoicingDetailSheetData[];
};

export const ContractInvoicingContext =
  createContext<ContractInvoicingContextType>(
    {} as ContractInvoicingContextType,
  );

export const ContractInvoicingProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const { getAllValue, keys } = useQueryParams();
  const selectedCustomerId = keys?.customerId as string;

  const contractIds = getAllValue(InvoicingQueryParams.CONTRACT_ID);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projects, setProjects] = useState<IProject[]>([]);
  const [formattedInvoicingDetails, setFormattedInvoicingDetails] = useState<
    InvoicingDetailSheetData[]
  >([]);
  const { getLocalStorageData, xeroTrackingItems } = useInvoicingContext();

  const invoicingActions = useMemo(
    () =>
      getLocalStorageData() || {
        action: InvoicingActions.CREATE,
        for: InvoicingLocalKeys.CONTRACT,
      },
    [getLocalStorageData],
  );

  // Queries
  const { data: multipleContracts, ...isMultipleContractsLoading } =
    useGetMultiContractQuery(
      { contractIds: contractIds || [] },
      {
        skip:
          !contractIds?.length ||
          invoicingActions.for !== InvoicingLocalKeys.CONTRACT ||
          invoicingActions.action !== InvoicingActions.CREATE,
      },
    );

  const { data: singleContractInvoice, ...isSingleInvoiceLoading } =
    useGetSingleContractInvoiceQuery(
      { contractInvoiceId: contractIds?.[0] ?? "" },
      {
        skip:
          !contractIds?.length ||
          invoicingActions.for !== InvoicingLocalKeys.CONTRACT ||
          invoicingActions.action === InvoicingActions.CREATE,
      },
    );

  const [mutateInvoice, { isLoading: isMutating }] =
    useMuateContractInvoiceMutation();

  const constructDefaultValues = (): CreateDocketInvoicing => {
    if (
      invoicingActions.action === InvoicingActions.CREATE &&
      multipleContracts
    ) {
      const itemSheets = multipleContracts.contracts?.map((contract) => ({
        ...emptyInvoicingItems,
        description: contract?.description,
        price: contract?.claimedValue / 100,
        quantity: 1,
        trackingName: xeroTrackingItems ? xeroTrackingItems[0]?.id : undefined,
        trackingOption: xeroTrackingItems
          ? xeroTrackingItems[0]?.options[0]?.trackingOptionID
          : undefined,
      }));

      return {
        ...emptyInvoicing,
        action: InvoicingActions.CREATE,
        dueDate: undefined,
        itemSheets,
      };
    }

    if (singleContractInvoice) {
      return {
        id: singleContractInvoice?.id,
        referenceNumber: singleContractInvoice?.referenceNumber ?? "",
        dueDate: singleContractInvoice?.dueDate ?? "",
        action:
          invoicingActions.action === InvoicingActions.VIEW
            ? InvoicingActions.VIEW
            : InvoicingActions.UPDATE,
        invoiceFor: InvoicingLocalKeys.CONTRACT,
        documents: singleContractInvoice?.documents ?? [],
        notes: singleContractInvoice?.note ?? "",
        itemSheets:
          singleContractInvoice.items?.map((item) => ({
            ...emptyInvoicingItems,
            itemDetailId: item?.id ?? "",
            defaultItemName: item?.invoiceItem?.title ?? "",
            itemId: item?.invoiceItem?.id ?? undefined,
            description: item?.description ?? "",
            quantity: item?.quantity ?? 0,
            trackingName: item?.trackingName ?? undefined,
            trackingOption: item?.trackingOption ?? undefined,
            account: item?.accountCode ?? "",
            discount: item?.discount ?? 0,
            price: item?.price ?? 0,
            tax: item?.taxPercentage ?? 0,
            title: "",
          })) ?? [],
      };
    }

    return {
      ...emptyInvoicing,
      itemSheets: [emptyInvoicingItems],
    };
  };

  const formattedFormDefaultValues = useMemo(constructDefaultValues, [
    multipleContracts,
    singleContractInvoice,
    invoicingActions.action,
    xeroTrackingItems,
  ]);

  const isLoading = isMultipleContractsLoading || isSingleInvoiceLoading; //It is named as loading but it has multiple properties ie. error, refetch, more,,

  const mutateDocketInvoice = async (
    values: CreateDocketInvoicing,
    dirtyFields?: object,
  ): Promise<MutationResponse> => {
    const dataToSend = {
      ...values,
      claimsInvoiceIds: multipleContracts?.contracts?.map(
        (contract) => contract?.id,
      ),
      customerId: selectedCustomerId,
      projectId: selectedProjectId || multipleContracts?.projects?.[0]?.id,
      dueDate: values?.dueDate
        ? combineDateWithCurrentTime(values?.dueDate as Date)
        : undefined,
    };

    const existingDocuments = values.documents?.filter(
      (doc) => typeof doc === "string" && doc.startsWith("http"),
    );

    const httpDocuments = values.documents?.filter(
      (doc) => typeof doc === "object",
    );
    const filteredValue = filterEmptyValues(dataToSend);
    const formData = parseToFormData(
      filteredValue,
      ["documents"],
      invoicingActions.action === InvoicingActions.UPDATE ? dirtyFields : {},
    );
    if (httpDocuments?.length === 0) {
      formData.delete("documents");
    }
    if (
      invoicingActions.action === InvoicingActions.CREATE &&
      !Object.prototype.hasOwnProperty.call(dirtyFields, "dueDate")
    ) {
      formData.delete("dueDate");
    }
    formData.append("existingDocuments", JSON.stringify(existingDocuments));
    const res = await mutateInvoice({
      data: formData,
      method:
        invoicingActions.action === InvoicingActions.UPDATE ? "PUT" : "POST",
      id: invoicingActions.action === InvoicingActions.UPDATE ? values.id : "",
    });

    return res;
  };

  useEffect(() => {
    if (
      multipleContracts &&
      invoicingActions.action === InvoicingActions.CREATE
    ) {
      setProjects(multipleContracts.projects);
      const formattedContractArr = multipleContracts?.contracts?.map(
        (contract, index) => ({
          id: 0,
          createdAt: contract.createdAt ?? "",
          projectName: multipleContracts?.projects?.[index]?.name ?? "",
          customerName: multipleContracts?.projects?.[0]?.customer?.name ?? "",
          itemGroup: contract.itemName,
          quantity: 1,
        }),
      );
      setFormattedInvoicingDetails(
        // Filter out any undefined values and ensure the array contains only valid InvoicingDetailSheetData objects
        (formattedContractArr ?? []).filter(
          Boolean,
        ) as InvoicingDetailSheetData[],
      );
    }

    if (
      (invoicingActions.action === InvoicingActions.VIEW ||
        invoicingActions.action === InvoicingActions.UPDATE) &&
      singleContractInvoice
    ) {
      const formattedContractArr = singleContractInvoice?.items?.map(
        (item) => ({
          id: 0,
          createdAt: item?.createdAt,
          projectName: singleContractInvoice?.project?.name ?? "---",
          customerName: singleContractInvoice?.customer?.name ?? "---",
          itemGroup: item?.invoiceItem?.title,
          quantity: item?.quantity,
        }),
      );

      setFormattedInvoicingDetails(
        // Filter out any undefined values and ensure the array contains only valid InvoicingDetailSheetData objects
        (formattedContractArr ?? []).filter(
          Boolean,
        ) as InvoicingDetailSheetData[],
      );
    }
  }, [multipleContracts, singleContractInvoice, invoicingActions]);

  return (
    <ContractInvoicingContext.Provider
      value={{
        setSelectedProjectId,
        isMutating,
        isLoading,
        formattedFormDefaultValues,
        mutateDocketInvoice,
        projects,
        formattedInvoicingDetails,
      }}
    >
      {children}
    </ContractInvoicingContext.Provider>
  );
};

export const useContractInvoicing = (): ContractInvoicingContextType =>
  useContext(ContractInvoicingContext);
